import { Outlet, useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Command, CommandGroup, CommandItem, CommandList } from "design-system/molecules/Command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { ChevronDown, Globe, PencilLine, Plus, SearchIcon } from "lucide-react"
import { useGetBackofficeOrganizationsQuery } from "modules/backoffice/hooks/organizations/useGetBackofficeOrganizationsQuery"
import { ORGANIZATION_TYPE_TRANSLATIONS } from "modules/backoffice/lib/organizations/translations"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Route as OrganizationsListRoute } from "routes/_base/_auth/backoffice/organizations/list"
import {
  BackofficeOrganizationsSortFields,
  BackofficeOrganizationsSortFieldsEnum,
  OrganizationType,
  OrganizationTypeEnum,
} from "schema"
import { BackofficeOrganizationsTable } from "./BackofficeOrganizationsTable"

const ITEMS_PER_PAGE = 25

type OrganizationsSortFields = BackofficeOrganizationsSortFieldsEnum

export const BackofficeOrganizations = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const search = OrganizationsListRoute.useSearch()

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: OrganizationsListRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { query, setQuery } = useSearchQuery({
    search,
    fullPath: OrganizationsListRoute.fullPath,
    onSearchQueryChange: () => setPage(1),
  })

  const { onSort, sortBy, fields } = useSortBy<OrganizationsSortFields[]>({
    search,
    fullPath: OrganizationsListRoute.fullPath,
    fields: [...BackofficeOrganizationsSortFields],
  })

  const { data: organizations, isPending } = useGetBackofficeOrganizationsQuery({
    query: {
      query,
      page,
      total: ITEMS_PER_PAGE,
      sortBy: sortBy ?? { field: "id", order: "asc" },
      filterBy: search.filterBy,
    },
    onSuccess: (data) => {
      setCount(data.pageInfo.count)
    },
  })

  const selectedTypes = useMemo(() => {
    const current = search.filterBy?.type
    if (!current) return [] as OrganizationType[]
    return Array.isArray(current)
      ? (current as OrganizationType[])
      : ([current] as OrganizationType[])
  }, [search.filterBy?.type])

  function updateTypes(next: OrganizationType[]) {
    const newFilterBy = { ...search.filterBy }
    if (next.length === 0) {
      delete newFilterBy.type
    } else {
      newFilterBy.type = next
    }
    navigate({
      to: OrganizationsListRoute.fullPath,
      search: { ...search, filterBy: newFilterBy },
    })
  }

  function toggleType(value: OrganizationType | "all") {
    if (value === "all") return updateTypes([])
    const next = selectedTypes.includes(value)
      ? selectedTypes.filter((v) => v !== value)
      : [...selectedTypes, value]
    updateTypes(next)
  }

  const typeOptions = useMemo(() => {
    return [
      { value: "all", label: t("backoffice.organizations.allOrganizations") },
      {
        value: OrganizationTypeEnum.Client,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Client]),
      },
      {
        value: OrganizationTypeEnum.Partner,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Partner]),
      },
      {
        value: OrganizationTypeEnum.Freemium,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Freemium]),
      },
      {
        value: OrganizationTypeEnum.Integration,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Integration]),
      },
    ]
  }, [t])

  const selectedLabel = useMemo(() => {
    if (selectedTypes.length === 0) return t("backoffice.organizations.allOrganizations")
    const labelMap = new Map(typeOptions.map((o) => [o.value, o.label]))
    return selectedTypes.map((v) => labelMap.get(v)).join(", ")
  }, [selectedTypes, typeOptions, t])

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-2 justify-end">
        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <SearchQuery
            placeholder={t("backoffice.organizations.search")}
            setQuery={setQuery}
            defaultValue={query}
            icon={SearchIcon}
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-auto min-w-40 h-full justify-between">
                <span className="truncate text-left">{selectedLabel}</span>
                <ChevronDown className="h-5 w-5 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 min-w-56" align="start">
              <Command>
                <CommandList>
                  <CommandGroup>
                    {typeOptions.map((type) => {
                      const checked =
                        type.value === "all"
                          ? selectedTypes.length === 0
                          : selectedTypes.includes(type.value as OrganizationType)
                      return (
                        <CommandItem
                          key={type.value}
                          value={String(type.value)}
                          onSelect={() => toggleType(type.value as OrganizationType | "all")}
                          className="gap-2"
                        >
                          <Checkbox checked={checked} />
                          <span className="truncate">{type.label}</span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex justify-end gap-2 h-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="default" className="h-full shrink-0">
                <Plus className="size-4" strokeWidth={2} />
                <span>{t("backoffice.companies.createCompany")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-full max-w-72">
              <DropdownMenuItem
                onClick={() => navigate({ to: "/backoffice/organizations/list/create", search })}
                className="flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer"
              >
                <div className="flex items-center gap-1.5">
                  <div className="p-2 rounded bg-muted shrink-0">
                    <PencilLine className="size-4 text-primary" />
                  </div>
                  <span className="font-medium text-base">
                    {t("backoffice.companies.createFromForm")}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {t("backoffice.companies.createFromFormDescription")}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  navigate({ to: "/backoffice/organizations/list/create-from-website", search })
                }
                className="flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer"
              >
                <div className="flex items-center gap-1.5">
                  <div className="p-2 rounded bg-muted shrink-0">
                    <Globe className="size-4 text-primary" />
                  </div>
                  <span className="font-medium text-base">
                    {t("backoffice.companies.createFromWebsite")}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {t("backoffice.companies.createFromWebsiteDescription")}
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <BackofficeOrganizationsTable
          organizations={organizations}
          isPending={isPending}
          fields={fields}
          onSort={onSort}
          search={search}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          count={count}
        />
      </div>
      <Outlet />
    </>
  )
}
